import { useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Code, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const EMBED_URL = `${BACKEND_URL}/embed`;

export default function Integration() {
  const [copied, setCopied] = useState("");

  const copy = (id, text) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    toast.success("Copié dans le presse-papier");
    setTimeout(() => setCopied(""), 2000);
  };

  const snippetInternalScroll = `<!-- Configurateur Atelier Inter-Habitat — Scroll Interne (Recommandé) -->
<div id="aih-configurator-wrap" style="width:100%; max-width:1280px; margin:0 auto; height: 100vh; /* Ajustez 100vh selon la taille de votre menu WordPress */">
  <iframe
    id="aih-configurator-frame"
    src="${EMBED_URL}"
    title="Configurateur Atelier Inter-Habitat"
    style="border:0; display:block; width:100%; height:100%;"
    loading="lazy"
    allow="clipboard-write"
  ></iframe>
</div>`;

  const snippetResponsive = `<!-- Configurateur Atelier Inter-Habitat — Option 2 : hauteur auto (désactive le sticky) -->
<div id="aih-configurator-wrap" style="width:100%;max-width:1280px;margin:0 auto;">
  <iframe
    id="aih-configurator-frame"
    src="${EMBED_URL}"
    title="Configurateur Atelier Inter-Habitat"
    style="border:0;display:block;width:100%;height:1600px;transition:height .2s ease;"
    loading="lazy"
    allow="clipboard-write"
  ></iframe>
</div>
<script>
  (function(){
    window.addEventListener("message", function(e){
      if (!e.data || e.data.type !== "aih-configurator-resize") return;
      var frame = document.getElementById("aih-configurator-frame");
      if (frame && typeof e.data.height === "number") {
        frame.style.height = (e.data.height + 40) + "px";
      }
    });
  })();
</script>`;

  const shortcode = `[html]${snippetResponsive.replace(/\n/g, "\n")}[/html]`;

  return (
    <main data-testid="integration-page" className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 py-8 lg:py-12">
      <div className="mb-10">
        <p className="eyebrow mb-2">Intégration</p>
        <h1 className="text-3xl sm:text-4xl">Installer le configurateur sur votre site WordPress</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Copiez le code ci-dessous et collez-le dans une page WordPress via un bloc <b>HTML</b>
          {" "}(ou dans un shortcode). Le configurateur s'adapte automatiquement à la largeur de votre site.
        </p>
      </div>

      {/* Preview */}
      <section className="paper p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="eyebrow text-[10px]">URL du configurateur</p>
            <code data-testid="embed-url" className="font-mono text-sm">{EMBED_URL}</code>
          </div>
          <a href={EMBED_URL} target="_blank" rel="noreferrer" className="btn-secondary text-xs">
            Ouvrir <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </section>

      {/* Recommended snippet */}
      <section className="paper p-6 mb-8">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-heading text-2xl">Option 1 — Recommandée <span className="text-accent">(Scroll Interne)</span></h2>
          <button
            data-testid="copy-internal"
            onClick={() => copy("internal", snippetInternalScroll)}
            className="btn-primary text-xs"
          >
            {copied === "internal" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied === "internal" ? "Copié" : "Copier"}
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Le configurateur s'adapte à la hauteur de l'écran (<code>100vh</code>). Le défilement se fait <b>à l'intérieur de l'iframe</b>, ce qui permet au meuble 3D de rester parfaitement fixé (sticky) à l'écran pendant qu'on fait défiler les options.
        </p>
        <pre className="bg-foreground text-background text-xs rounded-xl p-5 overflow-x-auto font-mono leading-relaxed">
{snippetInternalScroll}
        </pre>
      </section>

      {/* Basic snippet */}
      <section className="paper p-6 mb-8">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-heading text-2xl">Option 2 — Hauteur Automatique <span className="text-muted-foreground text-sm font-normal">(Désactive le "sticky")</span></h2>
          <button
            data-testid="copy-responsive"
            onClick={() => copy("resp", snippetResponsive)}
            className="btn-primary text-xs"
          >
            {copied === "resp" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied === "resp" ? "Copié" : "Copier"}
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          L'iframe s'agrandit automatiquement pour afficher tout le contenu sans barre de défilement interne. <b>Attention :</b> Cette méthode empêche le meuble 3D de rester figé à l'écran lors du défilement.
        </p>
        <pre className="bg-foreground text-background text-xs rounded-xl p-5 overflow-x-auto font-mono leading-relaxed">
{snippetResponsive}
        </pre>
      </section>

      {/* Instructions WordPress */}
      <section className="paper p-6 mb-8">
        <h2 className="font-heading text-2xl mb-4">Comment l'installer sur WordPress (IONOS)</h2>
        <ol className="space-y-4 text-sm">
          {[
            "Connectez-vous à votre tableau de bord WordPress (wp-admin).",
            "Ouvrez la page où vous souhaitez afficher le configurateur (ou créez-en une nouvelle).",
            'Cliquez sur "+" pour ajouter un bloc, puis cherchez "HTML personnalisé" (ou "Custom HTML").',
            "Collez le code de l'Option 1 (recommandée) dans le bloc.",
            'Cliquez sur "Aperçu" pour vérifier, puis "Publier".',
            "Le configurateur apparaît dans votre page, avec la même charte graphique que sur la démo.",
          ].map((step, i) => (
            <li key={i} className="flex gap-3">
              <span className="w-7 h-7 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">
                {i + 1}
              </span>
              <span className="pt-0.5">{step}</span>
            </li>
          ))}
        </ol>
      </section>

      {/* Alternative shortcode */}
      <section className="paper p-6 mb-8">
        <h2 className="font-heading text-2xl mb-3 flex items-center gap-2">
          <Code className="w-5 h-5" /> Astuce — thème classique
        </h2>
        <p className="text-sm text-muted-foreground mb-3">
          Si votre thème n'accepte pas les iframes (rare), installez le plugin gratuit
          <b> « Insert HTML Snippet »</b> et collez le code dans un shortcode réutilisable.
          Certains thèmes désactivent aussi les iframes dans l'éditeur classique : passez alors
          par un bloc <b>HTML personnalisé</b> ou <b>Code</b>.
        </p>
      </section>

      {/* Post-quote notification */}
      <section className="paper p-6 mb-8">
        <h2 className="font-heading text-2xl mb-3">Événements JavaScript (avancé)</h2>
        <p className="text-sm text-muted-foreground mb-3">
          Vous pouvez écouter les événements envoyés par le configurateur pour, par exemple,
          déclencher un pixel de conversion Google Ads ou Meta lors de la création d'un devis.
        </p>
        <pre className="bg-foreground text-background text-xs rounded-xl p-5 overflow-x-auto font-mono leading-relaxed">
{`window.addEventListener("message", function(e){
  if (e.data && e.data.type === "aih-quote-created") {
    // e.data.quote = { id, reference, total }
    console.log("Nouveau devis:", e.data.quote.reference, e.data.quote.total, "€");
    // gtag('event', 'quote_created', { value: e.data.quote.total });
  }
});`}
        </pre>
      </section>

      <div className="text-center">
        <Link to="/devis" className="btn-secondary">Voir mes devis reçus</Link>
      </div>
    </main>
  );
}
